import React, { useEffect, useState, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import MobileAppSidebar from './MobileAppSidebar';
import { Box, Drawer, List, CssBaseline, AppBar, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, Collapse } from '@mui/material';
import { useUserContext } from '../../context/user_context/UserContext';
import { useOrganizationContext } from '../../context/organization_context/OrganizationContext';
import { useNavigate, useLocation } from 'react-router-dom';
import useNavBarItems from './NavBarItems';
import { useTranslation } from 'react-i18next';
import { Help, KeyboardArrowDown, KeyboardArrowRight, Settings } from '@mui/icons-material';
import Chatbot from '../chat_bot/ChatBot';
import { StringKeys } from '../../resources/strings/StringKeys';

const drawerWidth = 200;

//Desktop

export default function DesktopAppSidebar({ content, isCalendar }) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { firebaseUser, userRole } = useUserContext();
  const { organization, organizationRoles } = useOrganizationContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const menuItems = useNavBarItems();
  const [openItems, setOpenItems] = useState(JSON.parse(localStorage.getItem('openItems')) || {});
  const [showChatBot, setShowChatBot] = useState(false)

  const convertToUnderscore = (str) => {
    if (typeof str !== 'string') return '';
    return str.replace(/\//g, '').replaceAll(' ', '_').toLowerCase();
  };

  const filteredMenuItems = useMemo(() => {
    if (!userRole || !organizationRoles) return [];
    // The new NavBarItems component already handles all the permission logic
    // So we can simply return the items directly
    return menuItems;
  }, [userRole, organizationRoles, menuItems]);

  useEffect(() => {
    localStorage.setItem('openItems', JSON.stringify(openItems));
  }, [openItems]);

  if (!organization || !firebaseUser) return null;

  const handleNavigation = (route) => {
    navigate(route);
  };

  const handleClick = (label) => {
    setOpenItems((prev) => {
      const isCurrentlyOpen = !!prev[label];
      const newOpenItems = Object.keys(prev).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {});
      newOpenItems[label] = !isCurrentlyOpen;
      return newOpenItems;
    });
  };

  const handleSupportClick = () => {
    setShowChatBot(prevState => !prevState);
  };

  const drawerList = () => (
    <Box sx={{ width: drawerWidth }} role="presentation">
      <List style={{ marginTop: '0px' }}>
        {filteredMenuItems.map((item) => (
          <React.Fragment key={item.label}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => item.route ? handleNavigation(item.route) : handleClick(item.label)}
                selected={location.pathname === item.route}
                sx={{
                  backgroundColor: location.pathname === item.route ? 'rgba(0, 0, 0, 0.08)' : 'transparent'
                }}
              >
                {item.icon && <ListItemIcon sx={{ minWidth: '40px', mr: 0 }}>{item.icon}</ListItemIcon>}
                {item.subItems && (openItems[item.label] ? <KeyboardArrowDown/> : <KeyboardArrowRight />)}

                <ListItemText 
                  primary={item.translation_key === 'organization' ? item.label : t(item.translation_key)} 
                  sx={{ fontSize: '0.9em' }} 
                  disableTypography 
                />
              </ListItemButton>
            </ListItem>
            {item.subItems && (
              <Collapse in={openItems[item.label]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.subItems.map((subItem) => (
                    <ListItem
                      key={subItem.label}
                      disablePadding
                      sx={{ pl: 3 }}
                    >
                      <ListItemButton
                        onClick={() => handleNavigation(subItem.route)}
                        selected={location.pathname === subItem.route}
                        sx={{
                          backgroundColor: location.pathname === subItem.route ? 'rgba(0, 0, 0, 0.08)' : 'transparent'
                        }}
                      >
                        {subItem.icon && <ListItemIcon sx={{ minWidth: '40px', mr: 0 }}>{subItem.icon}</ListItemIcon>}
                        <ListItemText 
                          primary={t(subItem.translation_key)} 
                          sx={{ fontSize: '0.9em' }}
                          disableTypography 
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
      <Divider />
      {/* Support ListItem */}
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={handleSupportClick}>
            <ListItemIcon sx={{ minWidth: '40px', mr: 0 }}>
              <Help color="primary" />
            </ListItemIcon>
            <ListItemText
              sx={{ fontSize: '0.9em' }}
              disableTypography 
              primary={t(StringKeys.SUPPORT)} 
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => {
            navigate('/settings')
          }}>
            <ListItemIcon sx={{ minWidth: '40px', mr: 0 }}>
              <Settings color="primary" />
            </ListItemIcon>
            <ListItemText 
              sx={{ fontSize: '0.9em' }}
              disableTypography 
              primary={t(StringKeys.SETTINGS)} 
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: isDesktop ? 'flex' : 'block' }}>
      <CssBaseline />
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <MobileAppSidebar />
      </AppBar>

      {isDesktop && (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
          }}
        >
          <Box sx={{ overflow: 'auto', marginTop: '60px' }}>{drawerList()}</Box>
        </Drawer>
      )}

      {
        isCalendar ? (
          <Box component="main" sx={{ flexGrow: 1, pt: 3, marginTop: '40px' }}>
          {content}
          </Box>
        ) : (
          <Box component="main" sx={{ flexGrow: 1, p: 3, marginTop: '80px' }}>
          {content}
          </Box>
        )
      }
      <Chatbot show={showChatBot} onClose={() => setShowChatBot(false)} />
    </Box>
  );
}